import {
  informationCreateMutation,
  informationListQueryKey,
  informationRetrieveOptions,
  informationUpdateMutation,
  InformationWritable
} from '@crm/api'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Form, Input, Modal, notification, Spin } from 'antd'
import { useEffect } from 'react'

interface ActionInformationModalProps {
  id?: number | null
  isOpen: boolean
  onClose: () => void
}

export const ActionInformationModal = ({
  id,
  isOpen,
  onClose
}: ActionInformationModalProps) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()

  const { data, isLoading } = useQuery({
    ...informationRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  useEffect(() => {
    if (!data) return

    form.setFieldsValue(data)
  }, [data])

  const { mutate: create, isPending: isCreatePending } = useMutation({
    ...informationCreateMutation(),
    onSuccess: () => onSuccessMutation(),
    onError: (error) => showApiErrors(error, form)
  })

  const { mutate: edit, isPending: isEditPending } = useMutation({
    ...informationUpdateMutation(),
    onSuccess: () => onSuccessMutation(),
    onError: (error) => showApiErrors(error, form)
  })

  const onSuccessMutation = () => {
    invalidateQueries([informationListQueryKey()])
    onClose()
    form.resetFields()
    notification.success({
      message: t('success.success'),
      description: t('success.success_data_save')
    })
  }

  const onSubmit = (values: InformationWritable) => {
    return id
      ? edit({ path: { id: Number(id) }, body: values })
      : create({ body: values })
  }

  const title = id
    ? t('information.edit_information')
    : t('information.add_information')

  return (
    <Modal
      title={title}
      open={isOpen}
      onCancel={onClose}
      confirmLoading={isCreatePending || isEditPending}
      onOk={form.submit}
      cancelText={t('buttons.cancel')}
      okText={t('buttons.save')}
    >
      <Spin spinning={isLoading}>
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item name="title" label={t('information.title')}>
            <Input placeholder={t('information.title')} />
          </Form.Item>

          <Form.Item name="description" label={t('information.description')}>
            <Input.TextArea placeholder={t('information.description')} />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
}
